import React from 'react';
import Icon from '../../../components/AppIcon';
import Select from '../../../components/ui/Select';
import dataPrivacyService from '../../../services/dataPrivacyService';

const DataResidencyControls = ({ preferences, onUpdate }) => {
  const dataResidency = preferences?.dataResidency || {};
  const regions = dataPrivacyService?.getAvailableRegions();

  const handleRegionChange = (newRegion) => {
    onUpdate('dataResidency', {
      region: newRegion,
      migrationStatus: newRegion === dataResidency?.region ? 'completed' : 'pending'
    });
  };

  const getMigrationStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'text-green-600';
      case 'in_progress':
        return 'text-yellow-600';
      case 'pending':
        return 'text-blue-600';
      default:
        return 'text-gray-600';
    }
  };

  const getMigrationStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return 'CheckCircle2';
      case 'in_progress':
        return 'Loader2';
      case 'pending':
        return 'Clock';
      default:
        return 'AlertCircle';
    }
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-start gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon name="Globe" size={20} className="text-primary" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-semibold text-foreground">Data Residency Controls</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Choose where your data is stored to comply with regional data protection regulations
          </p>
        </div>
      </div>

      <div className="space-y-6">
        {/* Region Selection */}
        <div>
          <Select
            label="Data Storage Region"
            description="Select the geographic region where your data will be stored"
            options={regions}
            value={dataResidency?.region}
            onChange={handleRegionChange}
            searchable={false}
          />
        </div>

        {/* Migration Status */}
        <div className="bg-muted/30 rounded-lg p-4">
          <div className="flex items-center gap-3 mb-2">
            <Icon 
              name={getMigrationStatusIcon(dataResidency?.migrationStatus)} 
              size={20} 
              className={getMigrationStatusColor(dataResidency?.migrationStatus)} 
            />
            <h3 className="font-medium text-foreground">Migration Status</h3>
          </div>
          <p className="text-sm text-muted-foreground">
            {dataResidency?.migrationStatus === 'completed' && 'All data is stored in the selected region'}
            {dataResidency?.migrationStatus === 'in_progress' && 'Data migration is currently in progress'}
            {dataResidency?.migrationStatus === 'pending' && 'Data migration will begin after saving settings'}
          </p>
        </div>

        {/* Compliance Certifications */}
        <div>
          <h3 className="text-sm font-medium text-foreground mb-3">Regional Compliance</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {dataResidency?.complianceCertifications?.map((cert) => (
              <div key={cert} className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                <Icon name="Award" size={16} className="text-primary" />
                <span className="text-sm text-foreground">{cert}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Region Details */}
        <div className="bg-muted/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="MapPin" size={16} className="text-muted-foreground flex-shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium mb-1">Current Region: {dataResidency?.region}</p>
              <p>
                {dataResidency?.region === 'US' && 'Data is stored in AWS US East (Virginia) with redundancy in US West (Oregon). Complies with US data protection laws and SOC 2 standards.'}
                {dataResidency?.region === 'EU' && 'Data is stored in AWS EU West (Ireland) with redundancy in EU Central (Frankfurt). Fully GDPR compliant with data residency guarantees.'}
                {dataResidency?.region === 'APAC' && 'Data is stored in AWS APAC Southeast (Singapore) with redundancy in APAC Northeast (Tokyo). Complies with regional data protection regulations.'}
              </p>
            </div>
          </div>
        </div>

        {/* Warning Notice */}
        {dataResidency?.migrationStatus === 'pending' && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 flex items-start gap-3">
            <Icon name="AlertTriangle" size={16} className="text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="text-xs text-yellow-800">
              <p className="font-medium mb-1">Region Change Pending</p>
              <p>
                Changing your data storage region will initiate a migration process. This may take several hours 
                depending on the amount of data. Your application will remain accessible during migration.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DataResidencyControls;
